import React from 'react';

import { useIntl } from 'react-intl';
import { queryClient } from 'src/lib/react-query';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalErrorMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { toast } from 'src/hooks/useToast';
import { AccountingRange } from 'src/features/accountingRanges/utils/types';
import { LIST_ACCOUNTING_RANGES_RESOURCE } from 'src/features/accountingRanges/api/useListAccountingRanges';
import { useDeleteAccountingRange } from 'src/features/accountingRanges/api/useDeleteAccountingRange';
import { accountingRangesMessages } from 'src/features/accountingRanges/utils/messages';
import { useNavigate } from 'react-router-dom';

interface DeleteAccountingRangeFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAccountingRange: AccountingRange;
    children?: React.ReactNode;
}

function DeleteAccountingRangeFormModal<TData>({
    selectedAccountingRange,
    children,
}: DeleteAccountingRangeFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage } = useIntl();
    const navigate = useNavigate();

    const { mutate: deletePayStation } = useDeleteAccountingRange({
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: [LIST_ACCOUNTING_RANGES_RESOURCE] });
            setIsModalOpen(false);
            navigate(`/accounting-ranges`);
            toast({
                title: formatMessage(accountingRangesMessages.deleteAccountingRangeSuccess),
                description: formatMessage(accountingRangesMessages.deleteAccountingRangeSuccessDescription),
            });
        },
        onError: (error) => {
            if (error?.response?.status !== 400) return;
            const errorDetails = error.response?.data?.errors[0]?.detail;
            const errorCode = formatMessage(generalErrorMessages.somethingWentWrong);
            const errorMessage = errorDetails || formatMessage(generalErrorMessages.unknownError);

            toast({
                title: errorCode,
                description: errorMessage,
                variant: 'destructive',
            });
        },
    });

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...accountingRangesMessages.deleteAccountingRange },
                subTitle: {
                    ...accountingRangesMessages.deleteAccountingRangeSubtitle,
                    values: {
                        entity: selectedAccountingRange.name,
                    },
                },
                icon: ICON_MAP.DELETE,
                confirmationMessage: { ...accountingRangesMessages.confirmDeleteAccountingRange },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: accountingRangesMessages.deleteAccountingRange,
                callbackFn: () => {
                    return (
                        deletePayStation({
                            id: selectedAccountingRange.id,
                        }),
                        setIsModalOpen(false)
                    );
                },
            }}
            children={children}
        />
    );
}

export default DeleteAccountingRangeFormModal;
